/**
 * @matchday/client — flags.ts
 *
 * Flags + kill switches (KICKOFF §2.5): one `config`-table-backed feed,
 * snapshot fetch + realtime subscription, <2s propagation. The transport owns
 * the 2s SLA; this client applies changes SYNCHRONOUSLY on receipt so the SLA
 * is never spent client-side.
 *
 * Kill-switch semantics (contracts/config.ts): a `kill.*` flag with
 * enabled=true means the feature is OFF. `isKilled` exposes exactly that, so
 * call sites read correctly:  if (flags.isKilled("kill.vault_claims")) { …block… }
 *
 * The subscription is an injectable callback interface shaped like a Supabase
 * realtime channel (subscribe → handler, returns unsubscribe) so the real app
 * passes a thin adapter over `supabase.channel(...).on("postgres_changes", …)`
 * and the harness/tests pass an in-memory emitter.
 */
import { FlagState, type FlagKey } from "@matchday/contracts";
import { z } from "zod";

// ── Injectable subscription ──────────────────────────────────────────────────

export type FlagChangeHandler = (flag: FlagState) => void;
/** Supabase-realtime-shaped: register a handler, get back an unsubscribe. */
export type FlagSubscribe = (onFlag: FlagChangeHandler) => () => void;

/** In-memory subscription source for tests/harness. */
export function createManualFlagFeed(): {
  subscribe: FlagSubscribe;
  emit: (flag: FlagState) => void;
} {
  const handlers = new Set<FlagChangeHandler>();
  return {
    subscribe(onFlag) {
      handlers.add(onFlag);
      return () => handlers.delete(onFlag);
    },
    emit(flag) {
      for (const handler of [...handlers]) handler(flag);
    },
  };
}

// ── Client ───────────────────────────────────────────────────────────────────

const FlagSnapshot = z.array(FlagState);

export interface FlagsClientOptions {
  /** Snapshot endpoint base, e.g. dugout/config service. GET `${baseUrl}/flags`. */
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  /** Realtime feed; changes are applied synchronously as they arrive. */
  subscribe?: FlagSubscribe;
  /** Seed cache (harness determinism / SSR snapshot embedded in the shell). */
  initial?: FlagState[];
}

export interface FlagsClient {
  /** Fetch + apply the snapshot. Safe to call repeatedly (version-guarded). */
  load(): Promise<void>;
  /** Start applying realtime changes. Returns unsubscribe. */
  connect(): () => void;
  /** True ⇔ the kill flag exists and enabled=true (feature is OFF). */
  isKilled(key: FlagKey): boolean;
  /** Raw enabled bit (feature.* flags). Missing flag ⇒ false. */
  isEnabled(key: FlagKey): boolean;
  get(key: FlagKey): FlagState | undefined;
  snapshot(): FlagState[];
  /** Fires synchronously on every applied change for `key`. Returns unsubscribe. */
  onChange(key: FlagKey, listener: (flag: FlagState) => void): () => void;
  /** Fires on every applied change, any key. Returns unsubscribe. */
  onAnyChange(listener: (flag: FlagState) => void): () => void;
  /** Apply one validated change now (used internally; exposed for adapters). */
  apply(flag: FlagState): void;
}

export function createFlagsClient(options: FlagsClientOptions = {}): FlagsClient {
  const cache = new Map<FlagKey, FlagState>();
  const keyListeners = new Map<FlagKey, Set<(flag: FlagState) => void>>();
  const anyListeners = new Set<(flag: FlagState) => void>();
  let unsubscribe: (() => void) | null = null;

  function apply(candidate: FlagState): void {
    const flag = FlagState.parse(candidate); // runtime boundary — realtime rows are untrusted
    const existing = cache.get(flag.key);
    // Version guard: realtime and snapshot can race; stale versions never win.
    if (existing !== undefined && existing.version >= flag.version) return;
    cache.set(flag.key, flag);
    // Synchronous application on receipt — the <2s SLA belongs to the transport.
    const listeners = keyListeners.get(flag.key);
    if (listeners !== undefined) for (const fn of [...listeners]) fn(flag);
    for (const fn of [...anyListeners]) fn(flag);
  }

  if (options.initial !== undefined) {
    for (const flag of options.initial) apply(flag);
  }

  return {
    async load(): Promise<void> {
      if (options.baseUrl === undefined) return;
      const fetchImpl = options.fetchImpl ?? fetch;
      const res = await fetchImpl(`${options.baseUrl.replace(/\/$/, "")}/flags`);
      if (!res.ok) throw new Error(`flags snapshot failed: HTTP ${res.status}`);
      const flags = FlagSnapshot.parse(await res.json());
      for (const flag of flags) apply(flag);
    },

    connect(): () => void {
      if (options.subscribe === undefined) return () => undefined;
      if (unsubscribe === null) {
        unsubscribe = options.subscribe((flag) => apply(flag));
      }
      const stop = unsubscribe;
      return () => {
        stop();
        unsubscribe = null;
      };
    },

    isKilled(key: FlagKey): boolean {
      if (!key.startsWith("kill.")) return false;
      return cache.get(key)?.enabled === true;
    },

    isEnabled(key: FlagKey): boolean {
      return cache.get(key)?.enabled === true;
    },

    get(key: FlagKey): FlagState | undefined {
      return cache.get(key);
    },

    snapshot(): FlagState[] {
      return [...cache.values()];
    },

    onChange(key, listener): () => void {
      const existing = keyListeners.get(key);
      const set = existing ?? new Set<(flag: FlagState) => void>();
      if (existing === undefined) keyListeners.set(key, set);
      set.add(listener);
      return () => {
        set.delete(listener);
      };
    },

    onAnyChange(listener): () => void {
      anyListeners.add(listener);
      return () => {
        anyListeners.delete(listener);
      };
    },

    apply,
  };
}
